import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { adminAnalyticsApi } from '../utils/api';

const SECONDARY = '#e8a838';
const NAV_CARD = '#162231';
const BORDER_COLOR = '#2a3f56';
const TEXT_LIGHT = '#f0f2f5';
const TEXT_MUTED = '#8a9bb5';
const SURFACE = '#1e2d3d';
const TABLE_HEADER_BG = '#e8a838';
const TABLE_HEADER_TEXT = '#000000';

// ── Date helpers ─────────────────────────────────────────────
function fmtDate(d) { return d.toISOString().slice(0, 10); }
function daysAgo(n) {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - n);
  return { start: fmtDate(start), end: fmtDate(end) };
}

const PRESETS = [
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
  { label: 'All', days: null },
];

// ── KPI Card ─────────────────────────────────────────────────
function KpiCard({ label, value }) {
  return (
    <div style={{
      background: NAV_CARD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 10,
      padding: '20px 24px', flex: '1 1 150px', minWidth: 140
    }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: TEXT_MUTED, marginBottom: 8, fontFamily: 'IBM Plex Sans, sans-serif' }}>
        {label}
      </div>
      <div style={{ fontSize: '2rem', fontWeight: 700, color: TEXT_LIGHT, fontFamily: 'Oswald, sans-serif', lineHeight: 1 }}>
        {value ?? '—'}
      </div>
    </div>
  );
}

// ── Section heading ──────────────────────────────────────────
function SectionHeading({ children }) {
  return (
    <h3 style={{ fontFamily: 'Oswald, sans-serif', color: TEXT_LIGHT, fontSize: '1.1rem', fontWeight: 600, margin: '32px 0 12px', borderBottom: `2px solid ${SECONDARY}`, paddingBottom: 6 }}>
      {children}
    </h3>
  );
}

// ── Line chart (SVG) ─────────────────────────────────────────
const LINE_COLORS = ['#e8a838', '#5b9bd5', '#70c1b3', '#f25f5c', '#a78bfa'];
const LINE_KEYS = [
  { key: 'unique_viewers', label: 'Viewers' },
  { key: 'total_views', label: 'Views' },
  { key: 'sponsor_clicks', label: 'Sponsor' },
  { key: 'advert_clicks', label: 'Advert' },
  { key: 'redirect_clicks', label: 'Redirects' },
];

function TrendChart({ data }) {
  if (!data || data.length === 0) {
    return <div style={{ color: TEXT_MUTED, padding: '32px 0', textAlign: 'center', fontFamily: 'IBM Plex Sans, sans-serif' }}>No daily data in this range.</div>;
  }

  const W = Math.max(600, data.length * 16);
  const H = 220;
  const padTop = 20, padBottom = 40, padLeft = 48, padRight = 16;
  const plotW = W - padLeft - padRight;
  const plotH = H - padTop - padBottom;

  const allVals = data.flatMap(d => LINE_KEYS.map(k => d[k.key] || 0));
  const maxVal = Math.max(...allVals, 1);

  const xScale = (i) => padLeft + (i / Math.max(data.length - 1, 1)) * plotW;
  const yScale = (v) => padTop + plotH - (v / maxVal) * plotH;

  return (
    <div style={{ overflowX: 'auto', background: NAV_CARD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 10, padding: '16px 12px' }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, paddingLeft: 4 }}>
        {LINE_KEYS.map((k, ki) => (
          <span key={k.key} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: TEXT_MUTED, fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <span style={{ width: 12, height: 3, background: LINE_COLORS[ki], borderRadius: 2, display: 'inline-block' }} />
            {k.label}
          </span>
        ))}
      </div>
      <svg width={W} height={H} style={{ display: 'block' }}>
        {/* Y-axis grid */}
        {[0, 0.25, 0.5, 0.75, 1].map(f => {
          const y = yScale(f * maxVal);
          return (
            <g key={f}>
              <line x1={padLeft} y1={y} x2={W - padRight} y2={y} stroke={BORDER_COLOR} strokeWidth={0.5} />
              <text x={padLeft - 6} y={y + 3} textAnchor="end" fontSize={9} fill={TEXT_MUTED} fontFamily="IBM Plex Sans, sans-serif">
                {Math.round(f * maxVal)}
              </text>
            </g>
          );
        })}
        {/* Lines */}
        {LINE_KEYS.map((k, ki) => {
          const pts = data.map((d, i) => `${xScale(i)},${yScale(d[k.key] || 0)}`).join(' ');
          return <polyline key={k.key} points={pts} fill="none" stroke={LINE_COLORS[ki]} strokeWidth={1.5} strokeLinejoin="round" />;
        })}
        {/* X-axis labels (show ~10 evenly spaced) */}
        {data.map((d, i) => {
          const step = Math.max(1, Math.floor(data.length / 10));
          if (i % step !== 0 && i !== data.length - 1) return null;
          return (
            <text key={i} x={xScale(i)} y={H - 6} textAnchor="middle" fontSize={9} fill={TEXT_MUTED} fontFamily="IBM Plex Sans, sans-serif"
              transform={`rotate(-30, ${xScale(i)}, ${H - 6})`}>
              {d.date.slice(5)}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

// ── Sortable table ───────────────────────────────────────────
function SortableTable({ columns, data, emptyMsg }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const sorted = useMemo(() => {
    if (!sortCol) return data;
    return [...data].sort((a, b) => {
      const va = a[sortCol] ?? 0;
      const vb = b[sortCol] ?? 0;
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
  }, [data, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  if (!data || data.length === 0) {
    return <div style={{ color: TEXT_MUTED, padding: '24px 0', textAlign: 'center', fontFamily: 'IBM Plex Sans, sans-serif' }}>{emptyMsg}</div>;
  }

  const thStyle = {
    padding: '10px 12px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', userSelect: 'none',
    background: TABLE_HEADER_BG, color: TABLE_HEADER_TEXT, fontFamily: 'IBM Plex Sans, sans-serif',
    whiteSpace: 'nowrap'
  };
  const tdStyle = {
    padding: '10px 12px', fontSize: '0.85rem', color: TEXT_LIGHT, fontFamily: 'IBM Plex Sans, sans-serif',
    borderBottom: `1px solid ${BORDER_COLOR}`, whiteSpace: 'nowrap'
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: NAV_CARD, borderRadius: 8, overflow: 'hidden' }}>
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key} style={thStyle} onClick={() => handleSort(col.key)}>
                {col.label} {sortCol === col.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? NAV_CARD : SURFACE }}>
              {columns.map(col => (
                <td key={col.key} style={tdStyle}>{col.render ? col.render(row) : row[col.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────
export default function AdminAnalyticsPage() {
  const [dateRange, setDateRange] = useState(() => daysAgo(30));
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [activePreset, setActivePreset] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminAnalyticsApi.getSummary(dateRange.start, dateRange.end)
      .then(res => { if (!cancelled) setData(res.data.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.message || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateRange.start, dateRange.end]);

  const applyPreset = (days) => {
    setActivePreset(days);
    if (days === null) {
      setDateRange({ start: '2024-01-01', end: fmtDate(new Date()) });
    } else {
      setDateRange(daysAgo(days));
    }
  };

  const applyCustom = () => {
    if (customStart && customEnd) {
      setActivePreset(null);
      setDateRange({ start: customStart, end: customEnd });
    }
  };

  const kpis = data?.kpis;

  const btnBase = {
    border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: '0.8rem', fontWeight: 600,
    cursor: 'pointer', fontFamily: 'IBM Plex Sans, sans-serif', transition: 'background 0.15s'
  };

  const eventCols = [
    { key: 'event_name', label: 'Event', render: (r) => <Link to={`/events/${r.event_id}/analytics`} style={{ color: SECONDARY, textDecoration: 'none' }}>{r.event_name}</Link> },
    { key: 'status', label: 'Status' },
    { key: 'event_start_date', label: 'Start' },
    { key: 'event_end_date', label: 'End' },
    { key: 'unique_viewers', label: 'Viewers' },
    { key: 'total_views', label: 'Views' },
    { key: 'sponsor_clicks', label: 'Sponsor Clicks' },
    { key: 'advert_clicks', label: 'Advert Clicks' },
    { key: 'redirect_clicks', label: 'Redirects' },
    { key: 'ctr_pct', label: 'CTR %', render: (r) => `${r.ctr_pct}%` },
  ];

  const boardCols = [
    { key: 'title', label: 'Advert Board' },
    { key: 'assigned_event_count', label: 'Events' },
    { key: 'impressions_on_assigned_events', label: 'Impressions' },
    { key: 'total_clicks', label: 'Clicks' },
    { key: 'unique_clickers', label: 'Unique' },
    { key: 'click_rate_pct', label: 'Click Rate %', render: (r) => `${r.click_rate_pct}%` },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>
      <h2 style={{ fontFamily: 'Oswald, sans-serif', color: TEXT_LIGHT, fontSize: '1.6rem', fontWeight: 700, margin: '0 0 4px' }}>
        Platform Analytics
      </h2>
      <div style={{ color: TEXT_MUTED, fontSize: '0.82rem', marginBottom: 24, fontFamily: 'IBM Plex Sans, sans-serif' }}>
        {dateRange.start} &mdash; {dateRange.end}
      </div>

      {/* Date range bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 28 }}>
        {PRESETS.map(p => (
          <button key={p.label} onClick={() => applyPreset(p.days)}
            style={{ ...btnBase, background: activePreset === p.days ? SECONDARY : BORDER_COLOR, color: activePreset === p.days ? '#000' : TEXT_LIGHT }}>
            {p.label}
          </button>
        ))}
        <span style={{ width: 1, height: 24, background: BORDER_COLOR, margin: '0 4px' }} />
        <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
          style={{ background: NAV_CARD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 6, color: TEXT_LIGHT, padding: '5px 8px', fontSize: '0.8rem', fontFamily: 'IBM Plex Sans, sans-serif' }} />
        <span style={{ color: TEXT_MUTED, fontSize: '0.8rem' }}>to</span>
        <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
          style={{ background: NAV_CARD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 6, color: TEXT_LIGHT, padding: '5px 8px', fontSize: '0.8rem', fontFamily: 'IBM Plex Sans, sans-serif' }} />
        <button onClick={applyCustom}
          style={{ ...btnBase, background: SECONDARY, color: '#000' }}>Apply</button>
      </div>

      {/* Loading / Error */}
      {loading && <div style={{ color: TEXT_MUTED, padding: '40px 0', textAlign: 'center', fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading analytics...</div>}
      {error && <div style={{ color: '#f25f5c', padding: '40px 0', textAlign: 'center', fontFamily: 'IBM Plex Sans, sans-serif' }}>{error}</div>}

      {!loading && !error && data && (
        <>
          {/* KPI cards */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
            <KpiCard label="Active Events" value={kpis?.active_events?.toLocaleString()} />
            <KpiCard label="Unique Viewers" value={kpis?.unique_viewers?.toLocaleString()} />
            <KpiCard label="Total Page Views" value={kpis?.total_views?.toLocaleString()} />
            <KpiCard label="Sponsor Clicks" value={kpis?.total_sponsor_clicks?.toLocaleString()} />
            <KpiCard label="Advert Clicks" value={kpis?.total_advert_clicks?.toLocaleString()} />
            <KpiCard label="Redirects" value={kpis?.total_redirects?.toLocaleString()} />
          </div>

          {/* Daily trend */}
          <SectionHeading>Daily Trend</SectionHeading>
          <TrendChart data={data.daily_trend} />

          {/* Events table */}
          <SectionHeading>Events Performance</SectionHeading>
          <SortableTable columns={eventCols} data={data.events_summary} emptyMsg="No events with activity in this date range" />

          {/* Advert boards table */}
          <SectionHeading>Advert Boards Performance</SectionHeading>
          <SortableTable columns={boardCols} data={data.advert_boards_summary} emptyMsg="No advert board activity in this date range" />
        </>
      )}
    </div>
  );
}
